import {
    Children,
    createContext,
    isValidElement,
    useContext,
    useEffect,
    useRef,
    type CSSProperties,
    type KeyboardEvent,
    type ReactElement,
    type ReactNode,
} from "react";
import { clsx } from "clsx";
import { color, space } from "@tokens/index";
import { pressHandlers } from "../press";
import { Text } from "../Text";
import pressable from "../pressable.module.css";
import { Popover, type PopoverProps } from "./Popover";
import styles from "./Menu.module.css";

/**
 * Menu: a list of actions in a Popover.
 *
 * `Menu` adds `role="menu"`, roving focus (Arrow keys, Home, End), and closes
 * on Tab. On the `compact` viewport class a menu with more than three items
 * presents as a bottom sheet unless `sheetOnCompact` says otherwise. Label the
 * menu with `labelledBy` (a visible element) or `ariaLabel` (plain text).
 *
 * `MenuItem` is a 40px row. Selecting a `menuitem` closes the menu;
 * `menuitemcheckbox` and `menuitemradio` keep it open.
 */

export interface MenuProps
    extends Omit<PopoverProps, "role" | "initialFocus" | "sheetOnCompact" | "children"> {
    sheetOnCompact?: boolean;
    children: ReactNode;
}

export type MenuItemRole = "menuitem" | "menuitemcheckbox" | "menuitemradio";
export type MenuItemTone = "default" | "danger";

export interface MenuItemProps {
    label: string;
    icon?: ReactNode;
    description?: string;
    onSelect: () => void;
    disabled?: boolean;
    tone?: MenuItemTone;
    role?: MenuItemRole;
    checked?: boolean;
    testId?: string;
}

interface MenuContextValue {
    readonly close: () => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);

const ITEM_SELECTOR = '[role^="menuitem"]:not([aria-disabled="true"])';

function enabledItems(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
}

function countItems(children: ReactNode): number {
    return Children.toArray(children).filter(
        (child) => isValidElement(child) && child.type === MenuItem,
    ).length;
}

const listStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: space[1],
    minWidth: 180,
    outline: "none",
};

export function Menu({ sheetOnCompact, children, onClose, ...popover }: MenuProps): ReactElement {
    const listRef = useRef<HTMLDivElement>(null);
    const asSheet = sheetOnCompact ?? countItems(children) > 3;

    useEffect(() => {
        if (!popover.open) return;
        const list = listRef.current;
        if (list === null) return;
        const [first] = enabledItems(list);
        if (first !== undefined) {
            first.focus({ preventScroll: true });
        } else {
            list.focus({ preventScroll: true });
        }
    }, [popover.open]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const list = listRef.current;
        if (list === null) return;
        const items = enabledItems(list);
        if (items.length === 0) return;
        const index = items.findIndex((item) => item === document.activeElement);

        const focusAt = (next: number): void => {
            event.preventDefault();
            items[(next + items.length) % items.length]?.focus({ preventScroll: true });
        };

        switch (event.key) {
            case "ArrowDown":
                focusAt(index + 1);
                return;
            case "ArrowUp":
                focusAt(index <= 0 ? items.length - 1 : index - 1);
                return;
            case "Home":
                focusAt(0);
                return;
            case "End":
                focusAt(items.length - 1);
                return;
            case "Tab":
                event.preventDefault();
                onClose();
                return;
            default:
                return;
        }
    };

    return (
        <Popover {...popover} onClose={onClose} initialFocus="none" sheetOnCompact={asSheet}>
            <MenuContext.Provider value={{ close: onClose }}>
                <div
                    ref={listRef}
                    role="menu"
                    aria-labelledby={popover.labelledBy}
                    aria-label={popover.ariaLabel}
                    tabIndex={-1}
                    style={listStyle}
                    onKeyDown={handleKeyDown}
                >
                    {children}
                </div>
            </MenuContext.Provider>
        </Popover>
    );
}

export function MenuItem({
    label,
    icon,
    description,
    onSelect,
    disabled = false,
    tone = "default",
    role = "menuitem",
    checked,
    testId,
}: MenuItemProps): ReactElement {
    const menu = useContext(MenuContext);

    const handleSelect = (): void => {
        if (disabled) return;
        onSelect();
        if (role === "menuitem") {
            menu?.close();
        }
    };

    return (
        <button
            type="button"
            role={role}
            aria-checked={role === "menuitem" ? undefined : checked === true}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            tabIndex={-1}
            data-tone={tone}
            data-testid={testId}
            className={clsx(pressable.pressable, styles.item)}
            onClick={handleSelect}
            {...pressHandlers<HTMLButtonElement>({}, disabled)}
        >
            {icon !== undefined && (
                <span aria-hidden="true" className={styles.icon}>
                    {icon}
                </span>
            )}
            <span className={styles.body}>
                <Text role="body" style={{ color: "inherit" }} truncate>
                    {label}
                </Text>
                {description !== undefined && (
                    <Text role="caption" tone="muted" truncate>
                        {description}
                    </Text>
                )}
            </span>
        </button>
    );
}

export function MenuSeparator(): ReactElement {
    return (
        <div
            role="separator"
            aria-orientation="horizontal"
            style={{
                height: 1,
                background: color.border.subtle,
                margin: `${space[1]} ${space[2]}`,
            }}
        />
    );
}
